import {
  Dispatch, SetStateAction, useContext, useState
} from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import ADD_TO_CART_MUTATION, {
  IAddToCartData,
} from '@graphql/User/mutations/add-to-cart.graphql';

import getVendorVariantAnalyticsPayload, {
  getVendorVariantIterablePayload,
} from '@lib/vendorVariant/getVendorVariantAnalyticsPayload';
import logger from '@lib/logger';
import UserContext from '@context/User/user-context';
import { LocalCartContext } from '@context/LocalCart/LocalCartContext';
import {
  isInStock,
  IVendorVariantInternal,
} from '@models/VendorVariant/i-vendor-variant';
import {
  useAddProductToUserSavedMutation
} from '@graphql-gen/mutations/addProductToUserSaved.gen';
import { IVendorProductGroupingVendorVariant } from '@components/pages/ProductV3/shared/types';

const log = logger('useAddToCart');

export interface IAddToCartOptions {
  boardId?: string;
  listId?: string;
}

type CartUpdater = (
  options?: IAddToCartOptions
) => void;

interface IUseAddToCartOpts {
  location: string;
  vendorVariant: IVendorVariantInternal | IVendorProductGroupingVendorVariant;
}

interface ICartUpdateState {
  busy: boolean;
  buttonLabel: string;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  error?: ApolloError | undefined;
  isDoneAddingToCart: boolean;
  vendorVariant: IVendorVariantInternal;
}

const ADD_TO_CART = 'Add to Cart';
const ADDING_TO_CART = 'Adding to Cart';
export const ADDED_TO_CART = 'Added!';

const SAVE_FOR_LATER = 'Save for later';
const SAVING = 'Saving';
const SAVED = 'Saved!';

export default function useAddToCart({
  location,
  vendorVariant
}: IUseAddToCartOpts): [CartUpdater, ICartUpdateState] {
  const { user } = useContext(UserContext);
  const { handleLocalAddToCart, convertFromCartProductToUserSaved } = useContext(LocalCartContext);
  const isOutOfStockProduct = !isInStock(vendorVariant.availability);

  const initialLabelState = isOutOfStockProduct ? SAVE_FOR_LATER : ADD_TO_CART;
  const sendingRequestState = isOutOfStockProduct ? SAVING : ADDING_TO_CART;
  const finishedRequestState = isOutOfStockProduct ? SAVED : ADDED_TO_CART;

  const [isDoneAddingToCart, setIsDoneAddingToCart] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(initialLabelState);
  const [quantity, setQuantity] = useState(1);
  const [addToCartMutation, addToCartResponse] = useMutation<IAddToCartData>(
    ADD_TO_CART_MUTATION,
    {
      refetchQueries: ['cartForUser'],
    }
  );

  const [addToUserSaved, addToUserSavedResponse] = useAddProductToUserSavedMutation({
    refetchQueries: ['cartForUser'],
  });

  const busy = addToCartResponse.loading || addToUserSavedResponse.loading;
  const error = addToCartResponse.error || addToUserSavedResponse.error;

  const userContext = useContext(UserContext);

  const { addCartedVendorVariants } = userContext;

  const addToCart = async ({ boardId, listId }: IAddToCartOptions = {}) => {
    if (!vendorVariant.id || !vendorVariant) {
      log.error('VendorVariant product was not provided');
      return;
    }

    const vendorVariantId = vendorVariant.id.toString();

    if (!user) {
      setButtonLabel(sendingRequestState);
      setIsDoneAddingToCart(false);

      handleLocalAddToCart(
        {
          quantity,
          vendorVariant: vendorVariant as IVendorVariantInternal,
          location,
        }
      );

      if (isOutOfStockProduct) {
        convertFromCartProductToUserSaved(vendorVariantId);
      }

      setButtonLabel(finishedRequestState);
      setIsDoneAddingToCart(true);
      setTimeout(() => {
        setButtonLabel(initialLabelState);
        setIsDoneAddingToCart(false);
      }, 3000);

      return;
    }

    if (!isOutOfStockProduct) {
      try {
        setButtonLabel(sendingRequestState);
        setIsDoneAddingToCart(false);

        const { data: addToCartData } = await addToCartMutation({
          variables: {
            cartProduct: {
              boardId,
              quantity,
              vendorVariantId: vendorVariant.id,
            },
          },
        });

        const cartProduct = addToCartData?.createUpdateCartProduct;

        const { cartProducts = [] } = cartProduct?.cart || {};

        const addedVendorVariant = cartProducts.find(
          (cp) => cp.vendorVariantId === vendorVariantId
        )?.vendorVariant;

        addCartedVendorVariants(parseInt(vendorVariantId, 10));

        // @ts-ignore
        const analyticsPayload = getVendorVariantAnalyticsPayload(addedVendorVariant);
        const payload = {
          cartId: cartProduct?.cart!.id,
          cartQuantity: cartProduct?.quantity,
          cartQuantityChange: quantity,
          location,
          quantity: cartProduct?.quantity,
          products: cartProducts.map((cp) => ({
            quantity: cp.quantity,
            // @ts-ignore
            ...getVendorVariantIterablePayload(cp.vendorVariant),
          })),
          listId: undefined,
          ...analyticsPayload,
        };

        if (listId) {
          payload.listId = listId;
        }

        analyticsTrack('Product Added', payload, {
          email: user?.email
        });

        setButtonLabel(finishedRequestState);
        setIsDoneAddingToCart(true);
        setTimeout(() => {
          setButtonLabel(initialLabelState);
          setIsDoneAddingToCart(false);
        }, 3000);
      } catch (_error) {
        log.error('An error occurred adding item to cart', _error);
      }
    }

    if (user && isOutOfStockProduct) {
      setButtonLabel(sendingRequestState);

      const { data } = await addToUserSaved({
        variables: {
          vendorVariantId,
          quantity,
          boardId
        }
      });

      if (!error && !busy && data) {
        setButtonLabel(finishedRequestState);
      }
    }
  };

  return [
    addToCart,
    {
      busy,
      buttonLabel,
      quantity,
      setQuantity,
      error,
      isDoneAddingToCart,
      vendorVariant: vendorVariant as IVendorVariantInternal,
    },
  ];
}

